/**
 * Transition screen content definitions
 * 
 * Each transition introduces the next wizard step with:
 * - Unique ID for analytics tracking
 * - i18n keys for title and subtitle
 * - Optional illustration asset
 * - Whether the step can be skipped
 */

export interface TransitionContent {
  id: string;
  from: string;
  to: string;
  titleKey: string;
  subtitleKey: string;
  illustration?: string;
  skippable: boolean;
}

/**
 * Transition content for all intermediary screens
 * 
 * Order follows wizard flow:
 * Auth → Location → Music → Scenario → Preview → Subscription
 */
export const TRANSITION_CONTENT: TransitionContent[] = [
  {
    id: 'auth-to-location',
    from: 'auth',
    to: 'location',
    titleKey: 'onboarding.transition.location.title',
    subtitleKey: 'onboarding.transition.location.subtitle',
    illustration: undefined, // TODO: Add illustration asset
    skippable: false,
  },
  {
    id: 'location-to-music',
    from: 'location',
    to: 'music',
    titleKey: 'onboarding.transition.music.title',
    subtitleKey: 'onboarding.transition.music.subtitle',
    illustration: undefined, // TODO: Add illustration asset
    skippable: true, // Music selection can be skipped
  },
  {
    id: 'music-to-scenario',
    from: 'music',
    to: 'scenario',
    titleKey: 'onboarding.transition.scenario.title',
    subtitleKey: 'onboarding.transition.scenario.subtitle',
    illustration: undefined, // TODO: Add illustration asset
    skippable: false,
  },
  {
    id: 'scenario-to-preview',
    from: 'scenario',
    to: 'preview',
    titleKey: 'onboarding.transition.preview.title',
    subtitleKey: 'onboarding.transition.preview.subtitle',
    illustration: undefined, // TODO: Add illustration asset
    skippable: false,
  },
  {
    id: 'preview-to-subscription',
    from: 'preview',
    to: 'subscription',
    titleKey: 'onboarding.transition.subscription.title',
    subtitleKey: 'onboarding.transition.subscription.subtitle',
    illustration: undefined, // TODO: Add illustration asset
    skippable: false,
  },
];

/**
 * Get transition content by source step
 */
export function getTransitionByFrom(from: string): TransitionContent | undefined {
  return TRANSITION_CONTENT.find(t => t.from === from);
}

/**
 * Get transition content by target step
 */
export function getTransitionByTo(to: string): TransitionContent | undefined {
  return TRANSITION_CONTENT.find(t => t.to === to);
}

/**
 * Get transition content by ID
 */
export function getTransitionById(id: string): TransitionContent | undefined {
  return TRANSITION_CONTENT.find(t => t.id === id);
}

/**
 * Check if a step has an incoming transition screen
 */
export function hasTransitionBefore(step: string): boolean {
  return TRANSITION_CONTENT.some(t => t.to === step);
}

/**
 * Get all skippable transitions
 */
export function getSkippableTransitions(): TransitionContent[] {
  return TRANSITION_CONTENT.filter(t => t.skippable);
}

export default TRANSITION_CONTENT;
